import { ShippingMethodTypes } from "@features/checkout/types";
import type { CountryCode } from "@config/site/types";
import { hasHouseNumber } from "@lib/utils/address";
import type { DeliveryInitialValues } from "@lib/utils/checkoutHelper";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import { ADDRESS_VALIDATION_CODES, type AddressValidatorResult } from "./types";
import { getAddressValidator } from "./validateDeliveryAddress";

const PICKUP_METHOD_KEYS: string[] = [
    ShippingMethodTypes.CLICK_AND_COLLECT,
    ShippingMethodTypes.JAPAN_CLICK_AND_COLLECT,
    ShippingMethodTypes.PARCEL_SHOP,
];

/**
 * Markets whose street field is not "street + house number".
 * JP: streetName is the town name (町名, e.g. 神宮前) and the building number
 * goes in additionalAddressInfo, so a digit check on streetName always fails.
 */
const MARKETS_WITHOUT_HOUSE_NUMBER_IN_STREET: CountryCode[] = ["JP"];

/**
 * Check for a house number in the combined street field, for markets that have
 * no address validation of their own. Reads streetName only, so a number typed
 * into address line 2 does not count.
 */
export const checkHouseNumber = ({
    address,
    shippingMethodId,
    country,
}: {
    address: DeliveryInitialValues;
    shippingMethodId: string;
    country: CountryCode;
}): AddressValidatorResult => {
    // Markets with their own validator keep exactly the behaviour they had, so
    // this check stays out of their way and must not preempt that validator.
    if (getAddressValidator(country)) {
        return { isValid: true };
    }

    if (MARKETS_WITHOUT_HOUSE_NUMBER_IN_STREET.includes(country)) {
        return { isValid: true };
    }

    // Pickup addresses come from the store and the address form is not rendered,
    // so the shopper could not act on the modal.
    if (PICKUP_METHOD_KEYS.includes(getDeliveryMethodKey(shippingMethodId))) {
        return { isValid: true };
    }

    const streetName = address?.streetName?.trim();

    // An empty street is already blocked by the required rule on the field.
    if (!streetName || hasHouseNumber(streetName)) {
        return { isValid: true };
    }

    return {
        isValid: false,
        action: "modal",
        error: { type: ADDRESS_VALIDATION_CODES.MISSING_HOUSE_NUMBER },
    };
};
